import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { slugFromCC } from 'tf-post-parser';
import { getRoles, getUser } from '../../../helpers/token';
import { getLocationDetails } from '../../../helpers/getLocationDetails';
import staticMapUrl from '../../../helpers/staticMapUrl';
import { figtree, fraunces } from '../../Landing/fonts';
import { ArrowIcon } from '../../Landing/ui';
import AuthorName from '../../General/AuthorName';
import FutureImage from '../../FutureImage';
import menuTranslatorLogo from '../../../images/menu-translator.png';
import appStoreBadge from '../../../images/app-store.svg';
import playStoreBadge from '../../../images/play-store.svg';
import ProfileAvatar from '../../Profile/ProfileAvatar';
import FollowButton from '../../Profile/FollowButton';

const APP_STORE_URL =
  'https://apps.apple.com/app/apple-store/id6670431055?pt=122664205&ct=travelfeed-rail&mt=8';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.curlycorn.menutranslator&referrer=utm_source%3Dtravelfeed%26utm_medium%3Drail';

// Sidebar rail for very wide screens (>=1500px), where the margins are
// genuinely idle real estate: the author box and destination map live here
// (instead of below the post) for everyone, plus the conversion cards for
// logged-out readers. The in-article promo cards hide themselves at this
// width (see ArticleBody CSS), so readers see each pitch exactly once.
export default function ConversionRail({
  country,
  thumbUrl,
  location,
  author,
}) {
  const [isMember, setIsMember] = useState(false);
  const [viewerIsPaid, setViewerIsPaid] = useState(false);
  const [visible, setVisible] = useState(false);
  const ticking = useRef(false);
  const authorIsPaid = Boolean(
    author && author.roles && author.roles.indexOf('paid') !== -1,
  );
  const theme = useTheme();
  const mapImage = staticMapUrl(
    location,
    theme.palette.mode === 'dark',
    560,
    320,
  );
  const {
    locationTitle,
    locationSubtitle,
    country: locationCountry,
  } = getLocationDetails(location);

  useEffect(() => {
    if (getUser()) setIsMember(true);
    try {
      const roles = getRoles();
      if (roles && roles.indexOf('paid') !== -1) setViewerIsPaid(true);
    } catch {
      // ignore undecodable tokens
    }
    const update = () => {
      ticking.current = false;
      const nearBottom =
        window.scrollY + window.innerHeight >
        document.documentElement.scrollHeight - 600;
      setVisible(window.scrollY > 650 && !nearBottom);
    };
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <aside
      className={`${figtree.className} tf-rail`}
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? undefined : 'none',
      }}
      aria-hidden={!visible}
    >
      {/* The author — the old sidebar's author box, compact */}
      {author && (
        <div className="tf-rail-author tw-mb-4 tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-p-4 tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
          <div className="tw-flex tw-items-center tw-gap-3">
            <Link href={`/@${author.username}`} prefetch={false}>
              <ProfileAvatar
                authorAvatar={author.avatar}
                author={author.username}
                size={44}
              />
            </Link>
            <div className="tw-min-w-0">
              <p className="tw-m-0 tw-font-mono tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-[#8A958F]">
                Written by
              </p>
              <Link
                href={`/@${author.username}`}
                prefetch={false}
                className={`${fraunces.className} tw-block tw-truncate tw-text-[15px] tw-font-semibold tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] tw-no-underline hover:tw-text-primary-700 dark:hover:tw-text-primary-300`}
              >
                <AuthorName author={author} />
              </Link>
            </div>
          </div>
          <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2">
            <FollowButton author={author.username} btnstyle="pill" />
            <Link
              href={`/@${author.username}`}
              prefetch={false}
              className="tw-rounded-full tw-border tw-border-solid tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-px-3.5 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] tw-no-underline tw-transition-colors hover:tw-border-primary-600 hover:tw-text-primary-700 dark:hover:tw-text-primary-300"
            >
              View blog
            </Link>
          </div>
        </div>
      )}

      {/* Start your blog (logged-out readers of free members' posts only) */}
      {!isMember && !authorIsPaid && (
        <div className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
          {thumbUrl && (
            <div className="tw-p-3 tw-pb-0">
              <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#3A4D44]">
                <div className="tw-flex tw-items-center tw-gap-1 tw-border-0 tw-border-b tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#0F1D18] tw-px-2 tw-py-1.5">
                  <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#E8A8A2]" />
                  <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#EFD49B]" />
                  <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-[#A8CCB4]" />
                  <span className="tw-ml-1 tw-flex-1 tw-rounded-full tw-bg-[#FAF8F1] dark:tw-bg-[#15231E] tw-px-1.5 tw-text-center tw-font-mono tw-text-[8px] tw-leading-relaxed tw-tracking-wide tw-text-[#8A958F]">
                    your-name.com
                  </span>
                </div>
                <div className="tw-relative tw-aspect-[16/9]">
                  <FutureImage
                    src={thumbUrl}
                    alt=""
                    fill
                    sizes="256px"
                    maxSrcSetWidth={800}
                    className="tw-object-cover"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="tw-p-4">
            <p
              className={`${fraunces.className} tw-m-0 tw-text-[1.05rem] tw-font-semibold tw-leading-snug tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]`}
            >
              {country ? (
                <>
                  Your{' '}
                  <em className="tw-italic tw-text-primary-700 dark:tw-text-primary-300">
                    {country}
                  </em>{' '}
                  story deserves a home like this.
                </>
              ) : (
                <>Your travel stories deserve a home like this.</>
              )}
            </p>
            <Link
              href="/"
              prefetch={false}
              className="tw-mt-3 tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-primary-600 tw-px-4 tw-py-2.5 tw-text-[14px] tw-font-semibold tw-text-white tw-no-underline tw-shadow-[0_8px_24px_-10px_rgba(0,137,123,0.7)] tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-bg-primary-700 hover:tw-text-white"
            >
              Start your travel blog
              <ArrowIcon className="tw-h-3.5 tw-w-3.5" />
            </Link>
            <p className="tw-mb-0 tw-mt-2 tw-text-center tw-font-mono tw-text-[10px] tw-tracking-wide tw-text-[#8A958F]">
              From $19/year · Online in minutes
            </p>
          </div>
        </div>
      )}

      {/* The destination map (in-flow DestinationCard hides at this width) */}
      {mapImage && location && location.coordinates && (
        <div className="tf-rail-map tw-mt-4 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
          <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-px-4 tw-pb-2.5 tw-pt-3.5">
            <div className="tw-min-w-0">
              <p className="tw-m-0 tw-font-mono tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-[#8A958F]">
                The destination
              </p>
              <p
                className={`${fraunces.className} tw-m-0 tw-mt-0.5 tw-truncate tw-text-[15px] tw-font-semibold tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]`}
              >
                {locationSubtitle || locationTitle}
              </p>
            </div>
          </div>
          <Link
            href={`/map?zoom=6&latitude=${encodeURIComponent(
              location.coordinates.coordinates[1],
            )}&longitude=${encodeURIComponent(
              location.coordinates.coordinates[0],
            )}`}
            prefetch={false}
            className={`tw-block tw-px-3${locationCountry ? '' : ' tw-pb-3'}`}
          >
            <FutureImage
              src={mapImage}
              width={560}
              height={320}
              alt={`Map of ${locationSubtitle || locationTitle}`}
              className="tw-h-auto tw-w-full tw-rounded-lg tw-mix-blend-multiply dark:tw-mix-blend-screen"
              unoptimized
            />
          </Link>
          {locationCountry && (
            <div className="tw-px-4 tw-pb-3.5 tw-pt-2">
              <Link
                href={`/destinations/${slugFromCC(location.countryCode)}`}
                prefetch={false}
                className="tw-text-[12.5px] tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 tw-no-underline hover:tw-underline"
              >
                {locationCountry} travel guide →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Menu Translator — hidden only when the viewer or the post's
          author is a paying member (paying users never see ads). */}
      {!viewerIsPaid && !authorIsPaid && (
        <div className="tf-rail-menu tw-mt-4 tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-p-4 tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
          <div className="tw-flex tw-items-center tw-gap-3">
            <FutureImage
              src={menuTranslatorLogo}
              alt="Menu Translator"
              width={40}
              height={40}
              className="tw-h-10 tw-w-10 tw-rounded-[10px]"
            />
            <div className="tw-min-w-0">
              <p className="tw-m-0 tw-text-[13.5px] tw-font-semibold tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]">
                Menu Translator
              </p>
              <p className="tw-m-0 tw-font-mono tw-text-[9px] tw-uppercase tw-tracking-[0.18em] tw-text-[#8A958F]">
                From the TravelFeed team
              </p>
            </div>
          </div>
          <p className="tw-mb-0 tw-mt-2.5 tw-text-[12.5px] tw-leading-relaxed tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
            Understand every dish, not just the words. Photograph any menu and
            know exactly what to order.
          </p>
          <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-transition-transform tw-duration-200 hover:-tw-translate-y-0.5"
            >
              <Image
                src={appStoreBadge}
                alt="Download on the App Store"
                className="tw-h-[34px] tw-w-auto"
                unoptimized
              />
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-transition-transform tw-duration-200 hover:-tw-translate-y-0.5"
            >
              <Image
                src={playStoreBadge}
                alt="Get it on Google Play"
                className="tw-h-[34px] tw-w-auto"
                unoptimized
              />
            </a>
          </div>
        </div>
      )}

      <style jsx>{`
        .tf-rail {
          display: none;
        }
        @media (min-width: 1500px) {
          .tf-rail {
            display: block;
            position: fixed;
            top: 92px;
            right: 20px;
            width: 280px;
            z-index: 30;
            transition: opacity 0.3s ease;
          }
        }
        /* The rail must always fit the viewport whole — never cut off,
           never a second scrollbar. Shorter screens shed cards, and each
           shed card stays in the article flow instead (the in-flow
           cards' hide rules in style.css use the same thresholds). */
        @media (max-height: 1179px) {
          .tf-rail :global(.tf-rail-menu) {
            display: none;
          }
        }
        @media (max-height: 999px) {
          .tf-rail :global(.tf-rail-map) {
            display: none;
          }
        }
        @media (max-height: 699px) {
          .tf-rail :global(.tf-rail-author) {
            display: none;
          }
        }
      `}</style>
    </aside>
  );
}
